import { lazy, Suspense, useEffect } from 'react';
import { FormHelperText, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import MaskedInput from 'react-text-mask';
import PropTypes from 'prop-types';

import { useAuth } from './use-auth';
import Layout from './Layout';
import TextField from './TextField';
import AuthForm from './AuthFormContainer';
import SwitchAuthLink from './SwitchAuthLink';
import SubmitButton from './SubmitButton';
import { useConfig } from '../bootstrap/ConfigProvider';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

function TextMaskPhone(props) {
    const { inputRef, ...other } = props;

    return (
        <MaskedInput
            {...other}
            ref={ref => {
                inputRef(ref ? ref.inputElement : null);
            }}
            mask={PHONE_MASK}
            placeholderChar={'\u2000'}
        />
    );
}
TextMaskPhone.propTypes = {
    inputRef: PropTypes.func.isRequired
};

const CreateNewAccount = () => {
    const classes = useStyles();
    const { t } = useTranslation();

    const { createNewAccount } = useAuth();
    const navigate = useNavigate();
    const { isMultiTenancyEnabled, requiresSingleTenantSignUp, captchaVerification } = useConfig();

    useEffect(() => {
        if (!isMultiTenancyEnabled && !requiresSingleTenantSignUp) {
            navigate('/login', { replace: true });
        }
    }, [navigate, isMultiTenancyEnabled, requiresSingleTenantSignUp]);

    const validate = values => {
        const errors = {};

        if (!values.firstName) {
            errors.firstName = t('CreateNewAccount.FirstNameRequired');
        } else if (values.firstName.length > 100) {
            errors.firstName = t('CreateNewAccount.CharLimit');
        }

        if (!values.lastName) {
            errors.lastName = t('CreateNewAccount.LastNameRequired');
        } else if (values.lastName.length > 100) {
            errors.lastName = t('CreateNewAccount.CharLimit');
        }

        if (!values.company) {
            errors.company = t('CreateNewAccount.CompanyRequired');
        } else if (values.company.length > 100) {
            errors.company = t('CreateNewAccount.CharLimit');
        }

        if (!values.email) {
            errors.email = t('CreateNewAccount.EmailRequired');
        } else if (!/\w+([-+._]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/.test(values.email)) {
            errors.email = t('CreateNewAccount.InvalidEmailFormat');
        }

        if (!values.phone) {
            errors.phone = t('CreateNewAccount.PhoneNumberRequired');
        }

        if (captchaVerification.enabled && !values.recaptchaToken) {
            errors.recaptchaToken = t('CreateNewAccount.CaptchaRequired');
        }

        return errors;
    };

    const onSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            const response = await createNewAccount(
                values.company,
                values.firstName,
                values.lastName,
                values.email,
                values.phone,
                values.recaptchaToken
            );

            if (response === true) {
                return navigate('/ThankYou');
            } else if (response?.error === 'Company already exists') {
                setErrors({ company: t('CreateNewAccount.CompanyAlreadyExists') });
            } else if (response?.error === 'Email already exists') {
                setErrors({ email: t('CreateNewAccount.EmailAlreadyExists') });
            } else {
                setErrors({ submitError: t('UnknownError') });
            }
        } catch {
            setErrors({ submitError: t('UnknownError') });
        }
        setSubmitting(false);
    };

    return (
        <Layout title={t('CreateNewAccount')}>
            <Formik
                validate={validate}
                onSubmit={onSubmit}
                validateOnBlur={false}
                initialValues={initialValues}
            >
                {({ errors, touched, isSubmitting, setFieldValue, setFieldTouched }) => (
                    <AuthForm>
                        <TextField name='firstName' label={t('FirstName')} autoFocus />
                        <TextField name='lastName' label={t('LastName')} />
                        <TextField name='company' label={t('Company')} />
                        <TextField name='email' label={t('Email')} />
                        <TextField
                            name='phone'
                            label={t('Phone')}
                            InputProps={{
                                inputComponent: TextMaskPhone
                            }}
                        />
                        <div>
                            <SubmitButton isSubmitting={isSubmitting}>
                                {t('CreateAccount')}
                            </SubmitButton>
                            {!!errors.submitError && (
                                <FormHelperText error>{errors.submitError}</FormHelperText>
                            )}
                        </div>

                        {captchaVerification.enabled && (
                            <div>
                                <Suspense fallback={null}>
                                    <div className={classes.captchaContainer}>
                                        <ReCAPTCHA
                                            sitekey={captchaVerification.siteKey}
                                            className={classes.captcha}
                                            onChange={value => {
                                                setFieldTouched('recaptchaToken', true);
                                                setFieldValue('recaptchaToken', value);
                                            }}
                                        />
                                    </div>
                                </Suspense>

                                {!!errors.recaptchaToken && touched.recaptchaToken && (
                                    <FormHelperText error>{errors.recaptchaToken}</FormHelperText>
                                )}
                            </div>
                        )}
                    </AuthForm>
                )}
            </Formik>
            <SwitchAuthLink
                message={t('CreateAccount.HaveAnAccount')}
                to='/login'
                linkLabel={t('Login')}
            />
        </Layout>
    );
};

export default CreateNewAccount;

const initialValues = {
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    recaptchaToken: null
};

const PHONE_MASK = [
    '+',
    '1',
    ' ',
    '(',
    /[1-9]/,
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/
];

// re-captcha seems to have some styling issues
// especially visible in dark backgrounds.
// trying to work-around the issues.
const useStyles = makeStyles(theme => ({
    captchaContainer:
        theme.palette.type === 'dark'
            ? {
                  borderRadius: 4,
                  overflow: 'hidden',
                  height: 65,
                  width: 267,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
              }
            : {},
    captcha:
        theme.palette.type === 'dark'
            ? {
                  maxWidth: 268,
                  transform: 'scale(0.888)',
                  transformOrigin: 'left center'
              }
            : {}
}));
